import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import AuthImage from "@/components/AuthImage";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, Printer, MapPin, User, Phone, Calendar, Hash, Boxes, Weight, FileText, Copy, Send, Receipt, RefreshCw, Pencil, Check, X as XIcon, Zap, CalendarClock, Warehouse } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon size={15} strokeWidth={1.5} className="text-[var(--text-3)] mt-0.5 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">{label}</div>
      <div className="text-sm text-[var(--text)] truncate">{value || "—"}</div>
    </div>
  </div>
);

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [events, setEvents] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");
  const [pricing, setPricing] = useState(null);
  const [recalcing, setRecalcing] = useState(false);
  const [editPallet, setEditPallet] = useState(false);
  const [palletDraft, setPalletDraft] = useState(0);
  const [palletNote, setPalletNote] = useState("");
  const [savingPallet, setSavingPallet] = useState(false);
  const [editRefs, setEditRefs] = useState(false);
  const [refsDraft, setRefsDraft] = useState({ reference_number: "", customer_order_number: "" });
  const [savingRefs, setSavingRefs] = useState(false);
  const [driverRoutes, setDriverRoutes] = useState([]);
  const [payoutDraft, setPayoutDraft] = useState({ route_id: "", payout: "", note: "" });
  const [savingPayout, setSavingPayout] = useState(false);
  const [calcingPayout, setCalcingPayout] = useState(false);
  const isPriv = user?.role === "admin" || user?.role === "dispatcher";
  const isCustomer = user?.role === "customer";
  const canAdjustPallets = order && order.status !== "zugestellt" && order.status !== "storniert" && (
    isPriv ||
    (isCustomer && order.customer_id === user?.customer_id)
  );
  const canEditRefs = order && (
    (isPriv && order.status !== "zugestellt" && order.status !== "storniert") ||
    (isCustomer && order.customer_id === user?.customer_id && ["erstellt", "geplant"].includes(order.status))
  );

  const load = async () => {
    try {
      const [o, t] = await Promise.all([
        api.get(`/orders/${id}`),
        api.get(`/orders/${id}/tracking`),
      ]);
      setOrder(o.data);
      setEvents(t.data);
      if (isPriv) {
        const [drv, pr] = await Promise.all([
          api.get("/drivers"),
          api.get(`/orders/${id}/pricing`).then(r => r.data).catch(() => null),
        ]);
        setDrivers(drv.data);
        setPricing(pr);
        // Hydrate driver-payout draft + driver-routes if a driver is already assigned
        const dId = o.data.assigned_driver_id;
        if (dId) {
          try {
            const { data: routes } = await api.get(`/drivers/${dId}/routes`);
            setDriverRoutes(routes || []);
          } catch { setDriverRoutes([]); }
        }
        setPayoutDraft({
          route_id: o.data.driver_route_id || "",
          payout: o.data.driver_payout_chf != null ? String(o.data.driver_payout_chf) : "",
          note: o.data.driver_payout_note || "",
        });
      }
    } catch (e) { setError("Auftrag konnte nicht geladen werden."); }
  };

  const recalcPrice = async () => {
    setRecalcing(true);
    try {
      const { data } = await api.post(`/orders/${id}/calculate-price`);
      setPricing(data);
    } catch (e) { setError("Preis-Neuberechnung fehlgeschlagen."); }
    finally { setRecalcing(false); }
  };

  const downloadInvoice = async () => {
    const token = localStorage.getItem("nv_token");
    const res = await fetch(`${API}/admin/billing/invoice/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { setError("Rechnungs-PDF konnte nicht geladen werden."); return; }
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const startEditPallet = () => {
    setPalletDraft(order?.pallet_count || 1);
    setPalletNote("");
    setEditPallet(true);
  };
  const cancelEditPallet = () => { setEditPallet(false); setPalletNote(""); };
  const savePalletCount = async () => {
    const n = Number(palletDraft);
    if (!n || n < 1) { setError("Bitte gültige Paletten-Menge eingeben."); return; }
    setSavingPallet(true); setError("");
    try {
      const { data } = await api.patch(`/orders/${id}/pallet-count`, { pallet_count: n, note: palletNote || null });
      setOrder(data);
      setEditPallet(false);
      // Reload pricing after recalc settles
      setTimeout(async () => {
        try {
          const pr = await api.get(`/orders/${id}/pricing`).then(r => r.data).catch(() => null);
          setPricing(pr);
        } catch { /* ignore */ }
      }, 800);
      // Reload tracking events to show the audit entry
      try {
        const t = await api.get(`/orders/${id}/tracking`);
        setEvents(t.data);
      } catch { /* ignore */ }
    } catch (e) { setError(formatApiError(e)); }
    finally { setSavingPallet(false); }
  };

  const startEditRefs = () => {
    setRefsDraft({
      reference_number: order?.reference_number || "",
      customer_order_number: order?.customer_order_number || "",
    });
    setEditRefs(true);
  };
  const saveRefs = async () => {
    setSavingRefs(true); setError("");
    try {
      const { data } = await api.patch(`/orders/${id}/references`, refsDraft);
      setOrder(data);
      setEditRefs(false);
      try {
        const t = await api.get(`/orders/${id}/tracking`);
        setEvents(t.data);
      } catch { /* ignore */ }
    } catch (e) { setError(formatApiError(e)); }
    finally { setSavingRefs(false); }
  };

  useEffect(() => { load(); }, [id]);

  const downloadLabels = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("nv_token");
      const res = await fetch(`${API}/orders/${id}/labels.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF konnte nicht geladen werden");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `etiketten-${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setDownloading(false); }
  };

  const downloadProof = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("nv_token");
      const res = await fetch(`${API}/orders/${id}/delivery-proof.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Lieferschein konnte nicht geladen werden");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lieferschein-${order.order_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setDownloading(false); }
  };

  const resendProof = async () => {
    if (!window.confirm("Lieferschein per E-Mail erneut versenden an Kunde, Admin und Empfänger?")) return;
    setDownloading(true);
    setError("");
    try {
      const { data } = await api.post(`/orders/${id}/delivery-proof/resend`);
      window.alert(`Lieferschein an ${data.count} Empfänger gesendet:\n${data.sent_to.join("\n")}`);
    } catch (e) { setError(formatApiError(e)); }
    finally { setDownloading(false); }
  };

  const updateStatus = async (status) => {
    await api.put(`/orders/${id}`, { status });
    await load();
  };
  const assignDriver = async (driverId) => {
    await api.put(`/orders/${id}`, { assigned_driver_id: driverId, status: order.status === "erstellt" ? "geplant" : order.status });
    await load();
    // Pre-load driver routes so the payout dropdown is populated
    if (driverId) {
      try {
        const { data } = await api.get(`/drivers/${driverId}/routes`);
        setDriverRoutes(data || []);
      } catch { setDriverRoutes([]); }
    } else { setDriverRoutes([]); }
  };
  const savePayout = async () => {
    setSavingPayout(true); setError("");
    try {
      const amount = payoutDraft.payout === "" ? null : Number(payoutDraft.payout);
      await api.put(`/orders/${id}`, {
        driver_payout_chf: amount,
        driver_route_id: payoutDraft.route_id || null,
        driver_payout_note: payoutDraft.note || null,
      });
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSavingPayout(false); }
  };
  const calcPayoutFromTiers = async () => {
    setCalcingPayout(true); setError("");
    try {
      const { data } = await api.post(`/orders/${id}/calculate-payout`);
      setPayoutDraft(p => ({ ...p, payout: String(data.total), note: data.note || p.note }));
    } catch (e) { setError(formatApiError(e)); }
    finally { setCalcingPayout(false); }
  };

  if (!order) {
    return <Layout><div className="px-6 py-8 text-[var(--text-3)]">{error || "Laden…"}</div></Layout>;
  }

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto fade-in">
        <button onClick={() => navigate(-1)} data-testid="order-detail-back" className="flex items-center gap-2 text-xs uppercase tracking-wider text-[var(--text-2)] hover:text-[var(--text)]">
          <ArrowLeft size={14} strokeWidth={1.5} /> Zurück
        </button>

        <div className="mt-4 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Auftrag</div>
            <h1 className="text-3xl md:text-4xl font-mono font-semibold tracking-tight mt-2" data-testid="order-detail-number">{order.order_number}</h1>
            <div className="flex items-center gap-3 mt-3">
              <StatusBadge status={order.status} />
              {order.reference_number && <span className="text-xs text-[var(--text-3)]">Ref: {order.reference_number}</span>}
              {order.customer_order_number && <span className="text-xs text-[var(--text-3)]">Bestell-Nr.: {order.customer_order_number}</span>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isPriv && (
              <button onClick={() => navigate(`/admin/orders/${id}/edit`)} data-testid="order-edit-button" className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
                Bearbeiten
              </button>
            )}
            <button
              onClick={async () => {
                if (!window.confirm("Auftrag wirklich duplizieren? Es wird eine neue Auftragsnummer mit neuen Paletten-Barcodes erstellt.")) return;
                try {
                  const { data } = await api.post(`/orders/${id}/duplicate`);
                  const base = user?.role === "customer" ? "/portal/orders" : "/admin/orders";
                  // Customer goes to detail (no edit access); admin goes straight to edit form
                  const suffix = user?.role === "customer" ? "" : "/edit";
                  navigate(`${base}/${data.id}${suffix}`);
                } catch (e) { setError(e.response?.data?.detail || "Duplizieren fehlgeschlagen"); }
              }}
              data-testid="order-duplicate-button"
              className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Copy size={16} strokeWidth={1.8} /> Duplizieren
            </button>
            <button
              onClick={async () => {
                const name = window.prompt("Name für die Vorlage:", `Vorlage ${order.delivery?.city || "Auftrag"}`);
                if (!name) return;
                try {
                  await api.post(`/order-templates/from-order/${id}`, { name });
                  window.alert(`Vorlage „${name}“ gespeichert. Findbar unter Vorlagen.`);
                } catch (e) { setError(e.response?.data?.detail || "Vorlage konnte nicht gespeichert werden"); }
              }}
              data-testid="save-as-template-button"
              className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Zap size={16} strokeWidth={1.8} /> Als Vorlage
            </button>
            <button onClick={downloadLabels} disabled={downloading} data-testid="print-labels-button" className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2">
              <Printer size={16} strokeWidth={1.8} /> {downloading ? "Lade…" : "Etiketten"}
            </button>
            {order.status === "zugestellt" && (
              <button
                onClick={downloadProof}
                disabled={downloading}
                data-testid="download-proof-button"
                className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
              >
                <Printer size={16} strokeWidth={1.8} /> {downloading ? "Lade…" : "Lieferschein"}
              </button>
            )}
            {order.status === "zugestellt" && (
              <button
                onClick={resendProof}
                disabled={downloading}
                data-testid="resend-proof-button"
                title="Lieferschein per E-Mail an Kunde, Admin und Empfänger versenden"
                className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
              >
                <Send size={16} strokeWidth={1.8} /> Versenden
              </button>
            )}
            {isPriv && (
              <button
                onClick={downloadInvoice}
                data-testid="download-invoice-button"
                className="btn-ghost px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
              >
                <Receipt size={16} strokeWidth={1.8} /> Rechnung PDF
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-4 mt-8">
          {/* Vereinbarter Liefertermin — sichtbar für alle Rollen (Admin/Customer/Driver) */}
          {order.scheduled_appointment_at && (
            <div
              className="lg:col-span-3 card p-4 flex items-start gap-4"
              style={{ background: "var(--accent-muted)", borderColor: "rgba(98,183,72,0.4)" }}
              data-testid="appointment-banner"
            >
              <div className="p-2.5 rounded-sm shrink-0" style={{ background: "var(--header)" }}>
                <CalendarClock size={18} strokeWidth={1.6} style={{ color: "#D4AF37" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-eyebrow" style={{ color: "var(--header)" }}>Vereinbarter Liefertermin</div>
                <div className="text-xl md:text-2xl font-semibold mt-1" style={{ color: "var(--header)" }} data-testid="appointment-banner-value">
                  {(() => { try { return new Date(order.scheduled_appointment_at).toLocaleString("de-CH", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return order.scheduled_appointment_at; } })()}
                </div>
                {order.scheduled_appointment_note && (
                  <div className="text-xs text-[var(--text-2)] mt-1.5 whitespace-pre-wrap">{order.scheduled_appointment_note}</div>
                )}
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mt-2">Vom Fahrer mit Empfänger abgemacht</div>
              </div>
            </div>
          )}
          {user?.role === "driver" && order.driver_payout_chf != null && !user?.hide_payout && (
            <div className="lg:col-span-3 card p-4 flex items-center gap-4" style={{ background: "var(--accent-muted)" }} data-testid="driver-payout-readonly">
              <div className="p-2.5 rounded-sm" style={{ background: "var(--header)" }}>
                <Receipt size={18} strokeWidth={1.5} style={{ color: "#D4AF37" }} />
              </div>
              <div className="flex-1">
                <div className="label-eyebrow" style={{ color: "var(--header)" }}>Vereinbarte Vergütung</div>
                <div className="text-2xl font-semibold font-mono mt-1" style={{ color: "var(--header)" }}>
                  CHF {Number(order.driver_payout_chf).toFixed(2)}
                </div>
                {order.driver_payout_note && <div className="text-xs text-[var(--text-2)] mt-1">{order.driver_payout_note}</div>}
              </div>
            </div>
          )}
          <div className="card p-6">
            <div className="label-eyebrow mb-4" style={{ color: "#D4AF37" }}>Abholadresse</div>
            <Row icon={User} label="Name" value={order.pickup?.name} />
            <Row icon={MapPin} label="Adresse" value={`${order.pickup?.address || ""}, ${order.pickup?.zip || ""} ${order.pickup?.city || ""}`} />
          </div>
          <div className="card p-6">
            <div className="label-eyebrow mb-4" style={{ color: "#D4AF37" }}>Lieferadresse</div>
            <Row icon={User} label="Name" value={order.delivery?.name} />
            <Row icon={MapPin} label="Adresse" value={`${order.delivery?.address || ""}, ${order.delivery?.zip || ""} ${order.delivery?.city || ""}`} />
          </div>
          <div className="card p-6">
            <div className="label-eyebrow mb-4" style={{ color: "#D4AF37" }}>Daten</div>
            <Row icon={Calendar} label="Lieferdatum" value={formatDate(order.delivery_date)} />
            {order.status === "zwischenlager" && order.warehouse_location && (
              <Row icon={Warehouse} label="Zwischenlager" value={order.warehouse_location} />
            )}
            {order.delivery_time && <Row icon={Calendar} label="Lieferzeit" value={order.delivery_time} />}
            <Row icon={User} label="Ansprechperson" value={order.contact_person} />
            <Row icon={Phone} label="Telefon" value={order.contact_phone} />
            {order.customer_order_number && !canEditRefs && <Row icon={Hash} label="Bestell-Nr. (Kunde)" value={order.customer_order_number} />}
            {canEditRefs && (
              <div className="flex items-start gap-3 py-2" data-testid="refs-row">
                <Hash size={15} strokeWidth={1.5} className="text-[var(--text-3)] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] flex items-center justify-between">
                    <span>Referenz Nr. / Bestell-Nr.</span>
                    {!editRefs && (
                      <button
                        type="button"
                        onClick={startEditRefs}
                        data-testid="refs-edit-button"
                        className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[var(--text)] flex items-center gap-1"
                      >
                        <Pencil size={11} /> Anpassen
                      </button>
                    )}
                  </div>
                  {editRefs ? (
                    <div className="mt-2 space-y-2">
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--text-3)] block mb-1">Referenz Nr.</label>
                        <input
                          data-testid="refs-edit-reference"
                          className="input-base text-sm"
                          value={refsDraft.reference_number}
                          onChange={(e) => setRefsDraft({ ...refsDraft, reference_number: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-[var(--text-3)] block mb-1">Bestell-Nr. (Kunde)</label>
                        <input
                          data-testid="refs-edit-customer-order"
                          className="input-base text-sm"
                          placeholder="Optional · Ihre interne Bestellnummer"
                          value={refsDraft.customer_order_number}
                          onChange={(e) => setRefsDraft({ ...refsDraft, customer_order_number: e.target.value })}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={saveRefs} disabled={savingRefs} data-testid="refs-edit-save" className="btn-gold px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider flex items-center gap-1">
                          <Check size={12} /> {savingRefs ? "Speichern…" : "Übernehmen"}
                        </button>
                        <button type="button" onClick={() => setEditRefs(false)} data-testid="refs-edit-cancel" className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider flex items-center gap-1">
                          <XIcon size={12} /> Abbrechen
                        </button>
                      </div>
                      <p className="text-[10px] text-[var(--text-3)]">Änderungen erscheinen automatisch auf den Etiketten (PDF neu herunterladen).</p>
                    </div>
                  ) : (
                    <div className="text-sm text-[var(--text)] mt-0.5" data-testid="refs-display">
                      <div>Referenz: <span className="font-mono">{order.reference_number || "—"}</span></div>
                      <div>Bestell-Nr.: <span className="font-mono">{order.customer_order_number || "—"}</span></div>
                    </div>
                  )}
                </div>
              </div>
            )}
            {order.recipient_email && <Row icon={User} label="Empfänger E-Mail" value={order.recipient_email} />}
            <div className="flex items-start gap-3 py-2" data-testid="pallet-count-row">
              <Boxes size={15} strokeWidth={1.5} className="text-[var(--text-3)] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] flex items-center justify-between">
                  <span>Paletten</span>
                  {canAdjustPallets && !editPallet && (
                    <button
                      type="button"
                      onClick={startEditPallet}
                      data-testid="pallet-edit-button"
                      className="text-[10px] uppercase tracking-wider text-[#D4AF37] hover:text-[var(--text)] flex items-center gap-1"
                    >
                      <Pencil size={11} /> Anpassen
                    </button>
                  )}
                </div>
                {editPallet ? (
                  <div className="mt-2 space-y-2">
                    <input
                      type="number" min={1} max={200} step={1}
                      data-testid="pallet-edit-input"
                      autoFocus
                      className="input-base font-mono text-base"
                      value={palletDraft}
                      onChange={(e) => setPalletDraft(e.target.value)}
                    />
                    <input
                      type="text"
                      data-testid="pallet-edit-note"
                      placeholder="Grund (optional, z.B. 'beim Laden festgestellt')"
                      className="input-base text-sm"
                      value={palletNote}
                      onChange={(e) => setPalletNote(e.target.value)}
                    />
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={savePalletCount} disabled={savingPallet} data-testid="pallet-edit-save" className="btn-gold px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider flex items-center gap-1">
                        <Check size={12} /> {savingPallet ? "Speichern…" : "Übernehmen"}
                      </button>
                      <button type="button" onClick={cancelEditPallet} data-testid="pallet-edit-cancel" className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-wider flex items-center gap-1">
                        <XIcon size={12} /> Abbrechen
                      </button>
                    </div>
                    <p className="text-[10px] text-[var(--text-3)]">Etiketten, Barcodes & Preisberechnung werden automatisch aktualisiert.</p>
                  </div>
                ) : (
                  <div className="text-sm text-[var(--text)] font-mono">{order.pallet_count}</div>
                )}
              </div>
            </div>
            <Row icon={Weight} label="Gewicht" value={`${order.weight_kg} kg`} />
            {isPriv && order.price_chf != null && <Row icon={Hash} label="Preis" value={`CHF ${Number(order.price_chf).toLocaleString("de-CH")}`} />}
            {order.notes && <Row icon={FileText} label="Bemerkungen" value={order.notes} />}
          </div>
        </div>

        {/* Pricing Breakdown (Admin/Dispatcher only) */}
        {isPriv && (
          <div className="card p-6 mt-4" data-testid="pricing-breakdown-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Preisaufstellung</div>
                {pricing?.model_used && (
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{
                    borderColor: pricing.model_used === "flat" ? "rgba(212,175,55,0.5)" : "rgba(98,183,72,0.5)",
                    color: pricing.model_used === "flat" ? "#7A5A0E" : "#15803D",
                    background: pricing.model_used === "flat" ? "rgba(212,175,55,0.10)" : "rgba(98,183,72,0.10)",
                  }}>{pricing.model_used === "flat" ? "Pauschal" : "Staffel"}</span>
                )}
              </div>
              <button onClick={recalcPrice} disabled={recalcing} data-testid="recalc-price" className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2">
                <RefreshCw size={13} className={recalcing ? "animate-spin" : ""} /> Neu berechnen
              </button>
            </div>
            {!pricing || (pricing.lines || []).length === 0 ? (
              <div className="text-sm text-[var(--text-3)] py-2">
                Noch keine Preisaufstellung. Klicke auf <strong>Neu berechnen</strong> oder hinterlege Preise unter Kunde → Preise.
              </div>
            ) : (
              <>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                      <th className="text-left py-2">Beschreibung</th>
                      <th className="text-right py-2">Anz.</th>
                      <th className="text-right py-2">Einzel CHF</th>
                      <th className="text-right py-2">Total CHF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricing.lines.map((l, i) => (
                      <tr key={i} className="border-b border-[var(--border)]">
                        <td className="py-2 text-[var(--text)]">{l.label}</td>
                        <td className="py-2 text-right font-mono text-[var(--text-2)]">{l.qty != null ? l.qty : ""}</td>
                        <td className="py-2 text-right font-mono text-[var(--text-2)]">{l.unit_price != null ? Number(l.unit_price).toFixed(2) : ""}</td>
                        <td className="py-2 text-right font-mono font-semibold" style={{ color: l.kind === "fuel" ? "var(--text-2)" : "var(--text)" }}>{Number(l.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex justify-end mt-4">
                  <div className="text-right space-y-1">
                    <div className="text-xs uppercase tracking-wider text-[var(--text-3)]">Subtotal: <span className="text-[var(--text)] font-mono ml-2">CHF {Number(pricing.subtotal || 0).toFixed(2)}</span></div>
                    {pricing.fuel_surcharge > 0 && (
                      <div className="text-xs uppercase tracking-wider text-[var(--text-3)]">Treibstoffzuschlag: <span className="text-[var(--text)] font-mono ml-2">CHF {Number(pricing.fuel_surcharge).toFixed(2)}</span></div>
                    )}
                    <div className="mt-2 inline-block px-4 py-2 rounded-sm" style={{ background: "var(--header)", color: "white" }}>
                      <span className="text-xs uppercase tracking-wider mr-3">Gesamt</span>
                      <span className="font-mono text-lg" style={{ color: "var(--gold)" }}>CHF {Number(pricing.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                {pricing.note && <div className="mt-3 text-xs text-[var(--text-3)] italic">Hinweis: {pricing.note}</div>}
              </>
            )}
          </div>
        )}

        {isPriv && (
          <div className="card p-6 mt-4">
            <div className="label-eyebrow mb-4" style={{ color: "#D4AF37" }}>Disposition</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-2">Status setzen</label>
                <select
                  data-testid="order-status-select"
                  value={order.status}
                  onChange={(e) => updateStatus(e.target.value)}
                  className="input-base"
                >
                  {["erstellt","geplant","abgeholt","zwischenlager","unterwegs","zugestellt","storniert"].map(s =>
                    <option key={s} value={s}>{s}</option>
                  )}
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Fahrer zuweisen</label>
                <select
                  data-testid="order-driver-select"
                  value={order.assigned_driver_id || ""}
                  onChange={(e) => assignDriver(e.target.value)}
                  className="input-base"
                >
                  <option value="">— kein Fahrer —</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>

            {/* Driver payout (admin only, after a driver is assigned) */}
            {order.assigned_driver_id && (
              <div className="mt-5 pt-5 border-t border-[var(--border)]" data-testid="driver-payout-section">
                <div className="flex items-center gap-2 mb-3">
                  <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Fahrer-Pauschale</div>
                  <span className="text-[10px] text-[var(--text-3)] uppercase tracking-wider">(nur Admin sichtbar)</span>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="label-eyebrow block mb-1">Strecke aus Vorlage</label>
                    <select
                      data-testid="driver-payout-route"
                      className="input-base text-sm"
                      value={payoutDraft.route_id}
                      onChange={(e) => {
                        const rid = e.target.value;
                        const r = driverRoutes.find(x => x.id === rid);
                        setPayoutDraft(p => ({
                          ...p,
                          route_id: rid,
                          payout: r ? String(r.payout_chf) : p.payout,
                        }));
                      }}
                    >
                      <option value="">— Keine —</option>
                      {driverRoutes.map(r => <option key={r.id} value={r.id}>{r.name} · CHF {Number(r.payout_chf).toFixed(2)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Betrag (CHF)</label>
                    <input
                      type="number" min={0} step="0.01"
                      data-testid="driver-payout-amount"
                      className="input-base font-mono text-sm"
                      placeholder="z.B. 280.00"
                      value={payoutDraft.payout}
                      onChange={(e) => setPayoutDraft(p => ({ ...p, payout: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-1">Notiz (optional)</label>
                    <input
                      type="text"
                      data-testid="driver-payout-note"
                      className="input-base text-sm"
                      placeholder="z.B. Pauschal-Strecke"
                      value={payoutDraft.note}
                      onChange={(e) => setPayoutDraft(p => ({ ...p, note: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button
                    onClick={calcPayoutFromTiers}
                    disabled={calcingPayout}
                    data-testid="driver-payout-calc-tiers"
                    className="btn-ghost px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
                    title="Betrag aus den Staffelpreisen des Fahrers berechnen"
                  >
                    {calcingPayout ? "Berechne…" : "Nach Staffel berechnen"}
                  </button>
                  <button
                    onClick={savePayout}
                    disabled={savingPayout}
                    data-testid="driver-payout-save"
                    className="btn-gold px-4 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
                  >
                    {savingPayout ? "Speichern…" : "Pauschale speichern"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Pallets */}
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4">Paletten ({order.pallet_count})</h2>
          <div className="card overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                  <th className="text-left py-3 px-4">Position</th>
                  <th className="text-left py-3 px-4">Barcode</th>
                </tr>
              </thead>
              <tbody>
                {order.pallets?.map((p) => (
                  <tr key={p.id} className="border-b border-[var(--border)]">
                    <td className="py-3 px-4 font-mono text-[var(--text)]">{p.index}/{p.total}</td>
                    <td className="py-3 px-4 font-mono text-[var(--text)] tracking-widest" data-testid={`pallet-barcode-${p.index}`}>{p.barcode}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Positions */}
        {order.positions && order.positions.length > 0 && (
          <div className="mt-8" data-testid="order-positions-section">
            <h2 className="text-xl font-semibold mb-4">Positionen ({order.positions.length})</h2>
            <div className="card overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                    <th className="text-left py-3 px-4">#</th>
                    <th className="text-left py-3 px-4">Verpackung</th>
                    <th className="text-left py-3 px-4">Menge</th>
                    <th className="text-left py-3 px-4">Höhe</th>
                    <th className="text-left py-3 px-4">Gewicht</th>
                    <th className="text-left py-3 px-4">Artikel / ID</th>
                    <th className="text-left py-3 px-4">Beschreibung</th>
                  </tr>
                </thead>
                <tbody>
                  {order.positions.map((p, i) => (
                    <tr key={i} className="border-b border-[var(--border)]" data-testid={`position-${i}`}>
                      <td className="py-3 px-4 font-mono text-[var(--text-3)]">{i + 1}</td>
                      <td className="py-3 px-4 font-mono text-[var(--text)]">{String(p.packaging_type).startsWith("efz_") ? ({ efz_2rad: "E-Fahrzeug 2 Rad", efz_3rad: "E-Fahrzeug 3 Rad", efz_4rad: "E-Fahrzeug 4 Rad" }[p.packaging_type] || p.packaging_type) : `${p.packaging_type} cm`}</td>
                      <td className="py-3 px-4 font-mono text-[var(--text)]">{p.quantity}</td>
                      <td className="py-3 px-4 font-mono text-[var(--text-2)]">{p.height_cm ? `${p.height_cm} cm` : "—"}</td>
                      <td className="py-3 px-4 font-mono text-[var(--text-2)]">{p.weight_kg ? `${p.weight_kg} kg` : "—"}</td>
                      <td className="py-3 px-4 text-[var(--text)]">{p.product_identification || "—"}</td>
                      <td className="py-3 px-4 text-[var(--text-2)]">{p.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Services */}
        {order.services && (order.services.tier
          || order.services.delivery_ground_floor
          || order.services.delivery_floor_no_lift
          || order.services.delivery_above_third_floor) && (
          <div className="mt-8" data-testid="order-services-section">
            <h2 className="text-xl font-semibold mb-4">Liefer- & Entsorgungsservice</h2>
            <div className="card p-6 flex flex-wrap gap-2">
              {(() => {
                const tierLabels = {
                  bis_150kg: "1. Position bis 150 kg",
                  bis_250kg: "2. Positionen bis 250 kg",
                  ueber_250kg: "3. Positionen über 250 kg",
                };
                const chips = [];
                if (order.services.tier) chips.push({ k: "tier", l: tierLabels[order.services.tier] });
                if (order.services.delivery_ground_floor) chips.push({ k: "ground", l: "Lieferung an Verwendungsort (ebenerdig)" });
                if (order.services.delivery_floor_no_lift) chips.push({ k: "lift", l: "Stockwerklieferung ohne Lift (pro Etage)" });
                if (order.services.delivery_above_third_floor) chips.push({ k: "3floor", l: "ab 3. Etage" });
                return chips.map((c) => (
                  <span
                    key={c.k}
                    data-testid={`service-chip-${c.k}`}
                    className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] rounded-sm"
                  >
                    {c.l}
                  </span>
                ));
              })()}
            </div>
          </div>
        )}

        {/* Tracking timeline */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Statusverlauf</h2>
          <div className="card p-6 space-y-3">
            {events.length === 0 && <div className="text-[var(--text-3)] text-sm">Noch keine Ereignisse.</div>}
            {events.map((ev) => (
              <div key={ev.id} className="flex items-start gap-4 border-l-2 border-[#D4AF37]/40 pl-4 py-1">
                <div>
                  <div className="text-sm text-[var(--text)]"><StatusBadge status={ev.status} /></div>
                  <div className="text-xs text-[var(--text-3)] mt-1">
                    {formatDateTime(ev.created_at)} · {ev.actor_name || "System"}
                  </div>
                  {ev.notes && <div className="text-sm text-[var(--text)] mt-1">{ev.notes}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery proof */}
        {(order.delivery_photo_path || order.signature_path) && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4">Lieferbeleg</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {order.delivery_photo_path && (
                <div className="card p-4">
                  <div className="label-eyebrow mb-3" style={{ color: "#D4AF37" }}>Lieferfoto</div>
                  <AuthImage path={order.delivery_photo_path} alt="Lieferfoto" testid="order-delivery-photo" className="w-full rounded-sm border border-[var(--border)]" />
                </div>
              )}
              {order.signature_path && (
                <div className="card p-4">
                  <div className="label-eyebrow mb-3" style={{ color: "#D4AF37" }}>Unterschrift Empfänger</div>
                  <AuthImage path={order.signature_path} alt="Unterschrift" testid="order-signature" className="w-full bg-white rounded-sm border border-[var(--border)]" />
                </div>
              )}
            </div>
            {order.delivered_at && (
              <div className="text-xs text-[var(--text-3)] mt-2">
                Zugestellt am {formatDateTime(order.delivered_at)}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
